import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  checkClientAdminEmailExists,
  checkClientAdminMobileExists,
  checkClientManagerEmailExists,
  checkClientManagerMobileExists,
  createClientAdmin,
  createClientManager,
  editClientAdmin,
  editClientManager,
} from "rc-app/api/systemBusinessAdminActionApi";

export type PersonRole = "businessAdmin" | "systemAdmin";

const LABELS: Record<PersonRole, string> = {
  businessAdmin: "Business Admin",
  systemAdmin: "System Admin",
};

export interface PersonFormModalProps {
  role: PersonRole;
  mode: "create" | "edit";
  agencyId: string;
  /** Required for System Admin CREATE (the owning business admin's id). */
  abaId?: string;
  /** Prefill + target id for EDIT. */
  record?: { id: string; name: string; email: string; phone: string };
  /** Existing people of the same role, for the CREATE duplicate-check. */
  existing?: { email: string; phone: string }[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * One modal for both Business Admin + System Admin, create + edit. Same design
 * everywhere. In EDIT mode only the Name is editable (email/mobile are shown but
 * locked); CREATE validates + blocks duplicate email/mobile against `existing`.
 */
const PersonFormModal: React.FC<PersonFormModalProps> = ({
  role,
  mode,
  agencyId,
  abaId,
  record,
  existing = [],
  onClose,
  onSaved,
}) => {
  const isEdit = mode === "edit";
  const label = LABELS[role];

  const [form, setForm] = useState({
    name: record?.name || "",
    email_id: record?.email || "",
    mobile_number: record?.phone || "",
  });
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [saving, setSaving] = useState(false);
  const emailTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const validate = () => {
    const e: { [k: string]: string } = {};
    if (!form.name.trim()) e.name = "Name is required";
    // Email + mobile are only editable/validated on CREATE (locked in edit).
    if (!isEdit) {
      if (!form.email_id.trim()) e.email_id = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_id))
        e.email_id = "Enter a valid email address";
      else if (
        existing.some(
          (a) =>
            a.email.trim().toLowerCase() === form.email_id.trim().toLowerCase(),
        )
      )
        e.email_id = `A ${label} with this email already exists`;
      if (!form.mobile_number.trim())
        e.mobile_number = "Mobile number is required";
      else if (!/^[6-9]\d{9}$/.test(form.mobile_number))
        e.mobile_number = "Enter a valid 10-digit mobile number";
      else if (
        existing.some((a) => a.phone.trim() === form.mobile_number.trim())
      )
        e.mobile_number = `A ${label} with this mobile number already exists`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const change = (field: keyof typeof form, value: string) => {
    if (field === "mobile_number") {
      if (value && !/^\d*$/.test(value)) return;
      value = value.slice(0, 10);
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));

    // CREATE: live (debounced) backend duplicate check per field, so a repeat
    // email/mobile is flagged as soon as it's entered — independent of the
    // other field. (Email is looked up globally; mobile within the agency.)
    if (isEdit) return;
    const v = value.trim();
    if (field === "email_id") {
      if (emailTimer.current) clearTimeout(emailTimer.current);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return;
      emailTimer.current = setTimeout(async () => {
        const check =
          role === "systemAdmin"
            ? checkClientAdminEmailExists
            : checkClientManagerEmailExists;
        const dup = await check(v).catch(() => null);
        if (dup)
          setErrors((prev) => ({
            ...prev,
            email_id: `A ${label} with this email already exists`,
          }));
      }, 400);
    } else if (field === "mobile_number") {
      if (mobileTimer.current) clearTimeout(mobileTimer.current);
      if (!/^[6-9]\d{9}$/.test(v)) return;
      mobileTimer.current = setTimeout(async () => {
        const check =
          role === "systemAdmin"
            ? checkClientAdminMobileExists
            : checkClientManagerMobileExists;
        const dup = await check(v, agencyId).catch(() => null);
        if (dup)
          setErrors((prev) => ({
            ...prev,
            mobile_number: `A ${label} with this mobile number already exists`,
          }));
      }, 400);
    }
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        // Only the name is editable.
        const payload = { name: form.name.trim() };
        if (role === "systemAdmin") await editClientAdmin(payload, record!.id);
        else await editClientManager(payload, record!.id);
        toast.success(`${label} updated`);
        onSaved();
        return;
      }

      // CREATE — authoritative backend duplicate check (email looked up
      // globally, mobile within the agency). A match on EITHER blocks creation
      // — catches a repeat even when the loaded `existing` list is empty.
      const emailCheck =
        role === "systemAdmin"
          ? checkClientAdminEmailExists
          : checkClientManagerEmailExists;
      const mobileCheck =
        role === "systemAdmin"
          ? checkClientAdminMobileExists
          : checkClientManagerMobileExists;
      const [dupEmail, dupMobile] = await Promise.all([
        emailCheck(form.email_id.trim()).catch(() => null),
        mobileCheck(form.mobile_number.trim(), agencyId).catch(() => null),
      ]);
      const dupErrors: { [k: string]: string } = {};
      if (dupEmail)
        dupErrors.email_id = `A ${label} with this email already exists`;
      if (dupMobile)
        dupErrors.mobile_number = `A ${label} with this mobile number already exists`;
      if (Object.keys(dupErrors).length) {
        setErrors(dupErrors);
        setSaving(false);
        return;
      }

      if (role === "systemAdmin") {
        await createClientAdmin({
          agency_id: agencyId,
          agency_business_admin_id: abaId,
          keycloak_id: "",
          username: "",
          email_id: form.email_id.trim(),
          mobile_number: form.mobile_number.trim(),
          name: form.name.trim(),
        });
      } else {
        await createClientManager({
          agency_id: agencyId,
          keycloak_id: "",
          username: "",
          email_id: form.email_id.trim(),
          mobile_number: form.mobile_number.trim(),
          name: form.name.trim(),
        });
      }
      toast.success(`${label} created`);
      onSaved();
    } catch (_e) {
      toast.error(`Failed to ${isEdit ? "update" : "create"} ${label}`);
      setSaving(false);
    }
  };

  const field = (
    fieldLabel: string,
    key: keyof typeof form,
    placeholder: string,
    type = "text",
    locked = false,
  ) => (
    <div>
      <label className="text-[#9FB8AF] text-sm">{fieldLabel}</label>
      <input
        type={type}
        value={form[key]}
        placeholder={placeholder}
        disabled={locked}
        onChange={(e) => change(key, e.target.value)}
        className={`w-full mt-1 px-3 py-2 rounded-lg bg-[#11272E] text-white text-sm border focus:outline-none focus:ring-2 transition-colors placeholder:text-[#5E7682] ${
          locked ? "opacity-50 cursor-not-allowed" : ""
        } ${
          errors[key]
            ? "border-red-500 focus:ring-red-500"
            : "border-[#2C4D5A] focus:ring-[#008564] focus:border-[#008564]"
        }`}
      />
      {errors[key] && (
        <p className="text-red-400 text-xs mt-1">{errors[key]}</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-[#15333D] border border-[#2C4D5A] rounded-xl w-full max-w-md p-5 sm:p-6">
        <h3 className="text-white text-lg font-semibold">
          {isEdit ? `Edit ${label}` : `Add ${label}`}
        </h3>
        <p className="text-[#9FB8AF] text-sm mt-1">
          {isEdit
            ? "Only the name can be changed here."
            : role === "systemAdmin"
              ? "They’ll manage agents and leads within the agency."
              : "They’ll manage system admins, agents and leads for the agency."}
        </p>

        <div className="mt-5 flex flex-col gap-4">
          {field("Full Name", "name", "e.g. Priya Sharma")}
          {field("Email ID", "email_id", "name@company.com", "email", isEdit)}
          {field("Phone Number", "mobile_number", "10-digit mobile", "tel", isEdit)}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm text-[#C7D6DD] bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm text-white bg-[#008564] hover:bg-[#0a9b76] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
                ? "Save"
                : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonFormModal;
